/**
 * A curva ACES Filmic do three, em JavaScript, e a sua inversa.
 *
 * Com um composer montado, o three não aplica tone mapping aos materiais (ele só
 * o faz ao desenhar na tela) e a curva passa a ser aplicada em tela cheia, sobre
 * o quadro inteiro — inclusive sobre a cor de fundo, que no caminho direto sai
 * crua. Sem compensação o fundo mudaria de azul-marinho para quase preto toda
 * vez que o composer entra ou sai. A inversa permite escolher o fundo que, depois
 * da curva, volta a ser a cor pretendida.
 *
 * Espelha `ACESFilmicToneMapping` de `tonemapping_pars_fragment.glsl.js`
 * (three 0.186). Se o three mudar a curva, o teste de equivalência com valores
 * medidos no navegador é o que avisa.
 */

export type RGB = readonly [number, number, number]

// Matrizes do three, já em linha-maior (o GLSL as escreve transpostas).
const ENTRADA: readonly (readonly [number, number, number])[] = [
  [0.59719, 0.35458, 0.04823],
  [0.076, 0.90834, 0.01566],
  [0.0284, 0.13383, 0.83777],
]
const SAIDA: readonly (readonly [number, number, number])[] = [
  [1.60475, -0.53108, -0.07367],
  [-0.10208, 1.10813, -0.00605],
  [-0.00327, -0.07276, 1.07602],
]

function multiplicar(m: typeof ENTRADA, v: RGB): [number, number, number] {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
  ]
}

function ajusteRrtOdt(v: number): number {
  const a = v * (v + 0.0245786) - 0.000090537
  const b = v * (0.983729 * v + 0.432951) + 0.238081
  return a / b
}

const limitar = (x: number) => Math.min(1, Math.max(0, x))

/** RGB linear (HDR) → RGB linear de exibição, como o three com `toneMappingExposure`. */
export function aplicarAces(cor: RGB, exposicao: number): [number, number, number] {
  const escala = exposicao / 0.6
  const entrada = multiplicar(ENTRADA, [cor[0] * escala, cor[1] * escala, cor[2] * escala])
  const ajustada: [number, number, number] = [
    ajusteRrtOdt(entrada[0]),
    ajusteRrtOdt(entrada[1]),
    ajusteRrtOdt(entrada[2]),
  ]
  const saida = multiplicar(SAIDA, ajustada)
  return [limitar(saida[0]), limitar(saida[1]), limitar(saida[2])]
}

function resolver3x3(m: number[][], b: number[]): [number, number, number] | null {
  const det =
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
    m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
    m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0])
  if (Math.abs(det) < 1e-18) return null
  const colunaSubstituida = (c: number) => {
    const t = m.map((linha, i) => linha.map((x, j) => (j === c ? b[i] : x)))
    return (
      t[0][0] * (t[1][1] * t[2][2] - t[1][2] * t[2][1]) -
      t[0][1] * (t[1][0] * t[2][2] - t[1][2] * t[2][0]) +
      t[0][2] * (t[1][0] * t[2][1] - t[1][1] * t[2][0])
    ) / det
  }
  return [colunaSubstituida(0), colunaSubstituida(1), colunaSubstituida(2)]
}

/**
 * Cor linear que, passada por `aplicarAces`, resulta em `alvo`. Newton com
 * jacobiano numérico: a curva acopla os três canais pelas matrizes, então não dá
 * para inverter canal a canal. Devolve `null` se o alvo estiver fora do que a
 * curva alcança (por exemplo, branco puro) ou o método não convergir.
 */
export function inverterAces(alvo: RGB, exposicao: number): [number, number, number] | null {
  let x: [number, number, number] = [alvo[0], alvo[1], alvo[2]]
  const passo = 1e-5

  for (let iteracao = 0; iteracao < 40; iteracao++) {
    const atual = aplicarAces(x, exposicao)
    const erro: [number, number, number] = [atual[0] - alvo[0], atual[1] - alvo[1], atual[2] - alvo[2]]
    if (Math.max(Math.abs(erro[0]), Math.abs(erro[1]), Math.abs(erro[2])) < 1e-7) return x

    const jacobiano: number[][] = [[], [], []]
    for (let c = 0; c < 3; c++) {
      const deslocado: [number, number, number] = [x[0], x[1], x[2]]
      deslocado[c] += passo
      const f = aplicarAces(deslocado, exposicao)
      for (let l = 0; l < 3; l++) jacobiano[l][c] = (f[l] - atual[l]) / passo
    }

    const delta = resolver3x3(jacobiano, erro)
    if (!delta) return null
    x = [Math.max(0, x[0] - delta[0]), Math.max(0, x[1] - delta[1]), Math.max(0, x[2] - delta[2])]
  }

  const final = aplicarAces(x, exposicao)
  const resto = Math.max(Math.abs(final[0] - alvo[0]), Math.abs(final[1] - alvo[1]), Math.abs(final[2] - alvo[2]))
  return resto < 1e-4 ? x : null
}
